import { Type } from 'class-transformer';

export class InformationSource {
  id: number;
  organizationId: number;
  name: string;
  enabled: number;
  sortingPosition: number;
}

export class RegistrationReason {
  id: number;
  organizationId: number;
  name: string;
  enabled: number;
  sortingPosition: number;
}

export class RegistrationReasonAnswer {
  id: number;
  answer: string;

  constructor(answer: string = null) {
    this.answer = answer;
  }
}

export class Organization {
  id: number;
  name: string;
  personHasFullBirthdate: number;
  requirePersonAddress: number;
  enableInformationSource: number;
  enableRegistrationReason: number;
  newsProvider: number;
  personalDataAgreement: string;
  termsAndConditions: string;
  useCaptcha: number;
  email: string;
  afterRegistrationInfo: string;
  paymentNotes: string;
  @Type(() => InformationSource) informationSources?: InformationSource[];
  @Type(() => RegistrationReason) registrationReasons?: RegistrationReason[];
}

export class Country {
  code: string;
  localizedName: string;
  englishName: string;
}

export class Teacher {
  id: number;
  displayName: string;
  profileText?: string;
  profilePictureName?: string;
  profilePictureWidth?: number;
  profilePictureHeight?: number;
  organizationId: number;
}

export class City {
  id: number;
  name: string;
}

export class Venue {
  id: number;
  name: string;
  cityId: number;
  street: string;
  zipCode: string;
  organizationId: number;
  description?: string;

  @Type(() => City) city?: City;
  @Type(() => Organization) organization?: Organization;
}

export class Room {
  id: number;
  venueId: number;
  name: string;
  capacity: number;
  description?: string;
  @Type(() => Venue) venue: Venue;
}

export class PriceModifierType {
  code: string;
  priority: number;
  isDiscount: number;
  userSelectable: number;
}

export class PartialCourseDiscount {
  priceModifierId: number;
  periodUnit: string;
  periodCount: number;
  maxMultiplier: number;
}

export class SocialGroup {
  id: number;
  name: string;
  plural: string;
  accusative: string;
}

export class SocialDiscount {
  priceModifierId: number;
  @Type(() => SocialGroup) socialGroups: SocialGroup[];
}

export class PriceModifier {
  id: number;
  typeCode: string;
  coursePricingId: number;
  amount: number;
  asPercentage: number;
  name: string;
  description: string;
  @Type(() => PartialCourseDiscount) partialCourseDiscount: PartialCourseDiscount;
  @Type(() => PriceModifierType) type: PriceModifierType;
  @Type(() => SocialDiscount) socialDiscount: SocialDiscount;
}

export class Pricing {
  id: number;
  basicPrice: number;
  @Type(() => PriceModifier) priceModifiers: PriceModifier[];
}

export class OtEvent {
  id: number;
  name: string;
  @Type(() => Date) startDate: Date;
  startTime: string;
  endTime: string;
  registrationEnabled: boolean;
  description?: string;
  organizationId: number;
  pricingId: number;
  teacherId?: number;
  roomId: number;
  registrationCreatedEmailId: number;
  paymentReceivedEmailId: number;
  @Type(() => Teacher) teacher?: Teacher;
  @Type(() => Room) room: Room;
  @Type(() => Pricing) pricing: Pricing;
  @Type(() => Organization) organization?: Organization;
}

export class OtEventInfo {
  constructor(info: Partial<OtEventInfo>) {
    Object.assign(this, info);
  }

  full: boolean;
  @Type(() => OtEvent) otEvent: OtEvent;
  registrationCount: number;
  capacity: number;
  fillUpPercentage: number;
}

export class BankPaymentConfig {
  paymentTypeConfigId: number;
  bankName: string;
  bankNumber: string;
  bankAccountNumber: string;
  iban: string;
  bic: string;
  dayCountForPayment: number;
}

export class PaymentType {
  code: string;
  online: number;
}

export class TrustPayConfig {
  paymentTypeConfigId: number;
  accountIdenitifier: string;
  secretKey: string;
  languageCode: string;
  countryCode: string;
}

export class PaymentTypeConfig {
  id: number;
  organizationId: number;
  paymentTypeCode: string;
  enabled: number;
  name: string;
  description: string;

  @Type(() => BankPaymentConfig) bankPaymentConfig: BankPaymentConfig;
  @Type(() => PaymentType) paymentType: PaymentType;
  @Type(() => TrustPayConfig) trustPayConfig: TrustPayConfig;
}

export class PersonAddress {
  street: string;
  city: string;
  postcode: string;
  countryCode: string;
  @Type(() => Country)
  country: Country;
}

export class Person {
  firstName: string;
  lastName: string;
  @Type(() => Date) birthdate: Date;
  email: string;
  telephone: string;
  @Type(() => PersonAddress) address: PersonAddress;
}

export class RegistrationSocialDiscount {
  socialGroupId: number;
  @Type(() => SocialGroup) socialGroup: SocialGroup;
}

export class RegistrationPriceModifier {
  @Type(() => PersonAddress) priceModifier: PriceModifier;
  @Type(() => RegistrationSocialDiscount) registrationSocialDiscount?: RegistrationSocialDiscount;
}

export class Registration {
  price: number;
  notes: string;
  informationSourceAnswer: string;
  @Type(() => OtEvent) otEvent: OtEvent;
  @Type(() => Pricing) pricing: Pricing;
  @Type(() => PaymentTypeConfig) paymentTypeConfig: PaymentTypeConfig;
  @Type(() => RegistrationPriceModifier) registrationPriceModifiers: RegistrationPriceModifier[];
  @Type(() => Person) person: Person;
  @Type(() => RegistrationReasonAnswer) registrationReasonAnswers: RegistrationReasonAnswer[];
}
